import * as React from "react";
import { navigate } from "gatsby";
import { Alert, Button, Text } from "@uikit";
import Modal from "@components/common/modal";
import useUser from "@hooks/useUser";
import AuthUI from "@components/common/authUI";
import Textbox from "@uikit/textbox";
import Checkbox from "@uikit/checkbox";
import { useApp } from "@store/app";
import { useWindow } from "@hooks/useWindow";
import { ProviderT } from "@models/common";
import { invoke } from "@kalabo/golfsavers-common";

interface IProps {
  visible?: boolean;
}

const FindBooking = ({ visible }: IProps) => {
  const hideModals = useApp(($) => $.hideModals);
  const currentUrl = useApp(($) => $.currentUrl);

  const win = useWindow();
  const [tab, setTab] = React.useState<"login" | "find">("find");
  const [mode, setMode] = React.useState<ProviderT>("manage");
  const { user } = useUser();

  const findBooking = React.useMemo(() => {
    return (
      <Modal
        visible={visible}
        title="Find booking or Log in"
        hideCloseButton={false}
        maxWidth="500px"
        onClose={() => {
          hideModals();
        }}
      >
        <div className="relative gap-5 p-4 sm:p-6 pb-20 overflow-auto h-[calc(100dvh-43px)] sm:h-auto  noscrollbar ">
          <div className="flex">
            <div
              className={`flex flex-1 border-b-[2px] pb-3 cursor-pointer justify-center ${
                tab === "find" ? "border-green" : "border-black/30"
              } `}
              onClick={() => {
                setTab("find");
              }}
            >
              <Text
                size={17}
                leading={1.3}
                weight="semibold"
                color={tab === "find" ? "green" : "black30"}
              >
                Find Booking
              </Text>
            </div>
            <div
              className={`flex flex-1 border-b-[2px] pb-3 cursor-pointer  justify-center ${
                tab === "login" ? "border-green" : "border-black/30"
              } `}
              onClick={() => {
                setTab("login");
              }}
            >
              <Text
                size={17}
                leading={1.3}
                weight="semibold"
                color={tab === "login" ? "green" : "black30"}
              >
                Log in
              </Text>
            </div>
          </div>
          <div className="flex flex-col flex-1 ">
            {tab === "find" ? (
              <div>
                <BookingLocator
                  onSuccess={() => {
                    setTab("find");

                    hideModals();
                  }}
                  onError={() => {
                    alert(
                      "Unable to find booking.  Please check your details and try again."
                    );
                  }}
                />
              </div>
            ) : (
              <div className="py-5 flex flex-col">
                <Text size={15} leading={1.5} color="black80" className="mb-5">
                  Log into your account to view and manage your bookings.
                </Text>
                {win && (
                  <AuthUI
                    key={visible ? "visible" : "hidden"}
                    redirectUrl={currentUrl}
                    mode={mode}
                    onModeChange={(mode) => {
                      setMode(mode);
                    }}
                    onSignIn={() => {
                      hideModals();
                      navigate("/account/bookings");
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  }, [user, visible, mode, tab]);

  return <>{findBooking}</>;
};

interface IBookingLocatorProps {
  onSuccess: () => void;
  onError?: () => void;
}

const BookingLocator = ({ onSuccess, onError }: IBookingLocatorProps) => {
  const [surname, setSurname] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, showError] = React.useState(false);
  const [orderReference, setOrderReference] = React.useState("");
  const [confirmed, setConfirmed] = React.useState(false);
  const win = useWindow();

  const submit = React.useCallback(async () => {
    // remove all non numeric characters
    showError(false);
    setLoading(true);
    const orderRef = orderReference.replace(/\D/g, "");
    const response = await invoke("findCustomerOrder", {
      order_ref: orderRef,
      surname: surname,
    });

    if (response.status !== 200) {
      setLoading(false);
      return;
    }
    const data = response.body;
    setLoading(false);
    if (data.success) {
      if (win) {
        win.open(`https://bookings.golfsavers.com/${data.id}`, "_blank");
      }
      onSuccess();
    } else {
      showError(true);
    }
  }, [surname, orderReference, loading]);

  const isDisabled = React.useMemo(() => {
    return !surname || !orderReference || !confirmed;
  }, [surname, orderReference, confirmed]);

  return (
    <div className="py-5 flex flex-col gap-5">
      <Text size={15} leading={1.5} color="black80">
        Please enter your surname and order reference to access our secure
        client portal.
      </Text>{" "}
      <div className="flex flex-col gap-2">
        {error && (
          <Alert
            type="error"
            title="Unable to locate booking. Please check your details and try again."
          />
        )}
        <Textbox
          id="surname"
          autoFocus
          value={surname}
          placeholder="Enter surname"
          onChange={(e) => setSurname(e)}
        />
        <Textbox
          id="orderReference"
          addonBefore={<>ORD-</>}
          value={orderReference}
          placeholder="Enter order reference number"
          onChange={(e) => setOrderReference(e)}
        />
      </div>
      <Checkbox
        id="confirmed"
        horizontallyCentered={false}
        mode="small"
        label="Check this box to confirm that you have permission to manage this booking on behalf of the other golfer(s) or that you are the only golfer"
        onChange={(e) => {
          setConfirmed(e);
        }}
        value={confirmed}
      />
      <Button
        onClick={async () => {
          submit();
        }}
        disabled={isDisabled}
        size="large"
        color="green"
        loading={loading}
        width="full"
        text="Find Booking"
        className="!m-0"
      />
    </div>
  );
};

export default FindBooking;
